import React, { useEffect, useRef } from 'react';
import { useVendorConfig } from './VendorConfigContext';
import { useAuth } from './AuthContext';
import socketService from '../services/socketService';
import { KOT_EVENTS } from '../constants/socketEvents';
import { printKOT, KOTData } from '../services/localPrintService';

/**
 * AutoPrintProvider
 *
 * Provides global auto-print functionality for KOTs.
 * Listens to KOT_NEW events from Socket.IO and automatically prints them
 * when the autoPrintKotOnOrder setting is enabled.
 *
 * This provider should be wrapped inside VendorConfigProvider to access
 * vendor configuration settings.
 */

interface AutoPrintProviderProps {
  children: React.ReactNode;
}

export const AutoPrintProvider: React.FC<AutoPrintProviderProps> = ({ children }) => {
  const { configuration } = useVendorConfig();
  const { isVendor, token } = useAuth();

  // Track printed KOT numbers to prevent duplicates
  const printedKotsRef = useRef<Set<string>>(new Set());

  // Cleanup old entries periodically (keep last 100)
  const cleanupPrintedKots = () => {
    if (printedKotsRef.current.size > 100) {
      const entries = Array.from(printedKotsRef.current);
      printedKotsRef.current = new Set(entries.slice(-50));
    }
  };

  useEffect(() => {
    // Only set up auto-print listener for vendors with the setting enabled
    if (!isVendor || !token || !configuration?.autoPrintKotOnOrder) {
      return;
    }

    console.log('[AutoPrint] Setting up KOT auto-print listener');

    const handleKotNew = async (kotData: KOTData) => {
      // Validate KOT data
      if (!kotData || !kotData.kotNumber) {
        console.warn('[AutoPrint] Invalid KOT data received:', kotData);
        return;
      }

      // Check for duplicate (already printed)
      if (printedKotsRef.current.has(kotData.kotNumber)) {
        console.log('[AutoPrint] KOT already printed, skipping:', kotData.kotNumber);
        return;
      }

      console.log('[AutoPrint] Auto-printing KOT:', kotData.kotNumber);

      try {
        const result = await printKOT(kotData);

        if (result.success) {
          console.log('[AutoPrint] KOT printed successfully:', kotData.kotNumber);
          printedKotsRef.current.add(kotData.kotNumber);
          cleanupPrintedKots();
        } else {
          console.warn('[AutoPrint] KOT print failed:', result.error);
          // Don't add to printed set on failure - allow retry
        }
      } catch (err) {
        console.error('[AutoPrint] KOT print error:', err);
      }
    };

    // Register listener with socket service
    socketService.on(KOT_EVENTS.NEW, handleKotNew);

    return () => {
      console.log('[AutoPrint] Removing KOT auto-print listener');
      socketService.off(KOT_EVENTS.NEW, handleKotNew);
    };
  }, [isVendor, token, configuration?.autoPrintKotOnOrder]);

  return <>{children}</>;
};

export default AutoPrintProvider;
